import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AssistancePanel } from "@/components/draft/AssistancePanel";
import { DraftContextHeader } from "@/components/draft/DraftContextHeader";
import { DraftStepFooter } from "@/components/drafting/DraftStepFooter";
import { DraftEditor } from "@/components/draft/DraftEditor";
import { DraftMatterContextBar } from "@/components/draft/DraftMatterContextBar";
import { ErrorAlert } from "@/components/common/ErrorAlert";
import { Button } from "@/components/ui/button";
import { useDraftMatter } from "@/hooks/useDraftMatters";
import { draftDocumentHasContent, parseDraftDocumentModel, resolveEditorDocument } from "@/lib/draft-document-utils";
import type { DraftDocumentModel, SectionProvenance } from "@/types/drafting";

export function DraftWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const { matter, isLoading, error, refreshSilently, saveMatter, applyVersionContent } =
    useDraftMatter(id, { syncStepStatus: "drafting" });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastValidContent, setLastValidContent] = useState<unknown>(null);
  const pendingSaveRef = useRef<{ model: DraftDocumentModel; html: string } | null>(null);

  const currentVersion = useMemo(() => {
    if (!matter?.versions?.length) return null;
    if (matter.current_version_id) {
      const current = matter.versions.find((v) => v.id === matter.current_version_id);
      if (current) return current;
    }
    return matter.versions.reduce((latest, version) =>
      version.version_number > latest.version_number ? version : latest
    );
  }, [matter]);

  const sectionProvenance = useMemo(() => {
    const raw = currentVersion?.section_provenance;
    if (!Array.isArray(raw)) return [] as SectionProvenance[];
    return raw as SectionProvenance[];
  }, [currentVersion?.section_provenance]);

  const editorContentJson = useMemo(
    () => resolveEditorDocument(currentVersion?.content_json, lastValidContent),
    [currentVersion?.content_json, lastValidContent]
  );

  const contentSyncKey = useMemo(
    () => `${currentVersion?.id ?? ""}:${JSON.stringify(editorContentJson ?? null)}`,
    [editorContentJson, currentVersion?.id]
  );

  useEffect(() => {
    const parsed = parseDraftDocumentModel(currentVersion?.content_json);
    if (draftDocumentHasContent(parsed)) {
      setLastValidContent(currentVersion?.content_json);
    }
  }, [currentVersion?.content_json]);

  useEffect(() => {
    setHasUnsavedChanges(false);
    pendingSaveRef.current = null;
  }, [contentSyncKey]);

  const handleEditorChange = useCallback((model: DraftDocumentModel, html: string) => {
    if (!draftDocumentHasContent(model)) {
      return;
    }
    pendingSaveRef.current = { model, html };
    setHasUnsavedChanges(true);
  }, []);

  const handleSaveDraft = useCallback(async () => {
    if (!id || !pendingSaveRef.current) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const { model, html } = pendingSaveRef.current;
      await saveMatter({ content_json: model, content_html: html });
      pendingSaveRef.current = null;
      setHasUnsavedChanges(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save draft");
    } finally {
      setIsSaving(false);
    }
  }, [id, saveMatter]);

  if (isLoading) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center p-8">
        <p className="text-sm text-[var(--lm-muted)]">Loading draft workspace...</p>
      </div>
    );
  }

  if (error || !matter) {
    return (
      <div className="p-8">
        <ErrorAlert message={error ?? "Draft matter not found"} />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="min-w-0 shrink-0">
        <DraftContextHeader matter={matter} currentStep={3} />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-4 lg:flex-row lg:p-6">
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
          <DraftMatterContextBar matter={matter} />
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-[var(--lm-muted)] dark:text-slate-500">
              Use Copilot or clause variants to update the draft. Save manual edits when you are
              ready.
            </p>
            <div className="flex shrink-0 items-center gap-2">
              {hasUnsavedChanges ? (
                <span className="text-xs text-amber-700 dark:text-amber-300">Unsaved edits</span>
              ) : null}
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!hasUnsavedChanges || isSaving}
                onClick={() => void handleSaveDraft()}
              >
                {isSaving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
          {saveError ? <ErrorAlert message={saveError} className="mb-2" /> : null}
          <DraftEditor
            key={contentSyncKey}
            contentJson={editorContentJson}
            contentVersionKey={contentSyncKey}
            matter={matter}
            onChange={handleEditorChange}
          />
        </div>

        <div className="flex min-h-[min(420px,50dvh)] shrink-0 flex-col lg:min-h-0 lg:h-full lg:w-[360px] xl:w-[400px]">
          <AssistancePanel
            className="h-full"
            matterId={matter.id}
            contentJson={currentVersion?.content_json}
            sectionProvenance={sectionProvenance}
            onContentUpdated={(contentJson, versionId) => {
              const parsed = parseDraftDocumentModel(contentJson);
              if (draftDocumentHasContent(parsed)) {
                setLastValidContent(contentJson);
                applyVersionContent(contentJson, versionId);
              }
              void refreshSilently();
            }}
          />
        </div>
      </div>

      <DraftStepFooter matterId={matter.id} currentStep={3} />
    </div>
  );
}
